/**
 * Wire protocol 的信封定義。對應 docs/wire.md。
 *
 * 純型別與純函式。傳輸方式不是網路——是 postMessage，
 * 但這一層對「底下是什麼」一無所知，只認得 Port。
 */

import type { BusError, Message, NodeDef, StateSnapshot } from './types.ts'

export const WIRE_MAGIC = 1
export const WIRE_VERSION = '0.1'
export const SUPPORTED_WIRE_VERSIONS: readonly string[] = ['0.1']

// §7 逾時
export const HELLO_TIMEOUT_MS = 10_000
export const CONTROL_TIMEOUT_MS = 30_000
/** 跨邊界延遲的寬限，疊加在 kernel 的逾時之上 */
export const BOUNDARY_GRACE_MS = 5_000
export const ON_WRITE_TIMEOUT_MS = 60_000
export const ON_CALL_TIMEOUT_MS = 300_000

export type AppReqOp =
  | 'register'
  | 'add_nodes'
  | 'replace_nodes'
  | 'remove_nodes'
  | 'read'
  | 'read_many'
  | 'write'
  | 'call'
  | 'subscribe'
  | 'unsubscribe'
  | 'unregister'

/**
 * `touch`：有人真的碰了這個 app 的畫面。
 *
 * 射後不理，沒有回覆，也不影響任何 topic 的值——**它只是「這裡有人」**。
 *
 * 為什麼非要一條線不可：app 住在跨來源 iframe 裡，裡面的指標事件不會冒泡到宿主。
 * 而宿主有些東西要靠「有沒有人在操作」來決定，最明顯的是閒置就淡出的那條 dock：
 * 元件佔掉螢幕的絕大部分，所以「點了畫面卻什麼都沒醒過來」是常態不是例外。宿主
 * 唯一收得到的旁證是自己失焦，而那只在**焦點換到別的地方**時發生一次——在同一支
 * 元件裡連點十下，宿主一次都不知道。
 *
 * 只送 `isTrusted` 的事件，而且有節流：它是一個訊號，不是一串事件流。
 */
export type AppNoteOp = 'publish' | 'emit' | 'touch' | 'gesture'

/** `gesture` 的階段。end 不帶點，只表示手指離開了。 */
export type GesturePhase = 'start' | 'move' | 'end'

/**
 * 兩指手勢上的一個接觸點，座標是 `screenX` / `screenY`。
 *
 * **一定要用螢幕座標。** 用 iframe 內的 client 座標的話會產生一個回授迴圈：視
 * 窗被手勢移動 dx，iframe 跟著移動 dx，同一根沒動的手指在 iframe 裡的座標就變
 * 成 -dx——於是視窗會自己跑掉。螢幕座標與視窗在哪無關，所以差值就是差值。
 */
export interface GesturePoint {
  x: number
  y: number
}
export type HostReqOp = 'on_write' | 'on_call'

// ─────────────────────────────────────────────── 信封

export interface HelloFrame {
  qd: typeof WIRE_MAGIC
  t: 'hello'
  accepts: string[]
  sdk: string
}

export interface WelcomeFrame {
  qd: typeof WIRE_MAGIC
  t: 'welcome'
  protocol: string
  uid: string
  prefix: string
  kernel: string
}

export interface RefuseFrame {
  qd: typeof WIRE_MAGIC
  t: 'refuse'
  e: BusError
}

/** 請求。參數一律放在 `a`，避免與信封欄位混在同一層。 */
export interface ReqFrame {
  qd: typeof WIRE_MAGIC
  t: 'req'
  id: string
  op: string
  a: Record<string, unknown>
}

/** 射後不理。失敗時對方以 fault 非同步告知。 */
export interface NoteFrame {
  qd: typeof WIRE_MAGIC
  t: 'note'
  op: string
  a: Record<string, unknown>
}

export interface OkFrame {
  qd: typeof WIRE_MAGIC
  t: 'ok'
  id: string
  r: unknown
}

export interface ErrFrame {
  qd: typeof WIRE_MAGIC
  t: 'err'
  id: string
  e: BusError
}

export interface MsgFrame {
  qd: typeof WIRE_MAGIC
  t: 'msg'
  sub: number
  m: Message
}

export interface FaultFrame {
  qd: typeof WIRE_MAGIC
  t: 'fault'
  e: BusError
  ctx?: unknown
}

export interface ByeFrame {
  qd: typeof WIRE_MAGIC
  t: 'bye'
  reason: string
}

// ─────────────────────────────────────────────── 啟動交握（§9）
// 這兩個信封走 window.postMessage，不走 Port——Port 就是它們交出來的東西。

/** App → 桌面：我已載入完成且正在監聽，請把 Port 給我 */
export interface ReadyFrame {
  qd: typeof WIRE_MAGIC
  t: 'ready'
}

/** 桌面 → App：Port 在 event.ports[0] */
export interface PortFrame {
  qd: typeof WIRE_MAGIC
  t: 'port'
}

export type BootstrapFrame = ReadyFrame | PortFrame

export type Frame =
  | HelloFrame
  | WelcomeFrame
  | RefuseFrame
  | ReqFrame
  | NoteFrame
  | OkFrame
  | ErrFrame
  | MsgFrame
  | FaultFrame
  | ByeFrame
  | ReadyFrame
  | PortFrame

/**
 * §2：任何缺少 qd 標記的訊息一律靜默丟棄。
 * 一個頁面的 message 事件會收到擴充功能、第三方函式庫、開發工具的雜訊。
 */
export function isFrame(data: unknown): data is Frame {
  if (typeof data !== 'object' || data === null) return false
  const frame = data as { qd?: unknown; t?: unknown }
  return frame.qd === WIRE_MAGIC && typeof frame.t === 'string'
}

/** §3：挑出雙方都支援的最高版本 */
export function negotiate(
  accepts: readonly string[],
  supported: readonly string[] = SUPPORTED_WIRE_VERSIONS,
): string | null {
  const shared = supported.filter((version) => accepts.includes(version))
  if (shared.length === 0) return null
  return shared.sort(compareVersions).at(-1) ?? null
}

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

// ─────────────────────────────────────────────── 請求／回覆的載荷

export interface RegisterArgs {
  title: string
  summary: string
  groups?: Record<string, string>
  nodes: NodeDef[]
  initial_window?: unknown
}

export interface WriteArgs {
  path: string
  value: unknown
  timeout?: number
  /** 見 §6.3。app 端宣稱的，只能用於顯示 */
  gesture?: boolean
}

export interface CallArgs {
  path: string
  args: unknown
  timeout?: number
  gesture?: boolean
}

/**
 * SDK 端本地鏡像的快照。
 *
 * 刻意**沒有** stale 欄位——過期與否隨時間變動，遞送當下算出來的旗標
 * 本身就會過期。要準確的 stale 請用 read()（一次往返，由 kernel 計算）。
 */
export interface MirrorSnapshot {
  path: string
  value: unknown
  updated_at: number
  seq: number
}

export type { StateSnapshot }
